import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { GroupBase, OptionsOrGroups } from "react-select";
import { SelectItem } from "../components/select/Select";
import { CustomerQueryType, NetworkError } from "../common/models";
import { defaultSelectQuery } from "./tableHelpers";
import customersClient from "../api/private/customersClient";
import { handleApiErrorWithNotification } from "./errorHandling";

export const useCustomers = () => {
  const queryClient = useQueryClient();

  const loadCustomersForFilter = useCallback(
    async (
      phrase: string,
      callback: (
        options: OptionsOrGroups<SelectItem, GroupBase<SelectItem>>,
      ) => void,
      type: CustomerQueryType,
    ) => {
      try {
        // We cannot use useGetCustomrsQuery because it must be called on the top level of the component
        // so we use React-query cahce directly using queryClient
        const params = {
          ...defaultSelectQuery,
          searchPhrase: phrase,
          [type]: true,
        };

        // Use fetchQuery to leverage React Query's cache
        const result = await queryClient.fetchQuery({
          queryKey: ["customers", params],
          queryFn: () => customersClient.getCustomers(params),
          staleTime: 5 * 60 * 1000, // Cache for 5 minutes
        });

        callback(
          result.data.map((customer) => ({
            value: customer.id,
            label: customer.name,
          })),
        );
      } catch (e) {
        handleApiErrorWithNotification(e as NetworkError);
        callback([]);
      }
    },
    [queryClient],
  );

  return { loadCustomersForFilter };
};
